// src/pages/PedidosAutomaticos/Log.js
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import TableWithPagination from "./../common/TableWithPagination";
import InfoCards from '../common/InfoCards';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useApi } from './../../hooks/useApi';
import DetalleLogPanel from './../salesorders/DetalleLogPanel';

const Traduccion = () => {
  const getDefaultDateRange = () => {
    const today = new Date().toISOString().split('T')[0];
    return {
      from: `${today}T00:00:00`,
      to: `${today}T23:59:00`,
    };
  };

  const { get } = useApi();

  const [logs, setLogs] = useState([]);
  const [selectedProceso, setSelectedProceso] = useState('');
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [statusFilter, setStatusFilter] = useState('');

  const [selectedLog, setSelectedLog] = useState(null);
  const [detalleLog, setDetalleLog] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const formatDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '');

  const fetchDetalleLog = async (log) => {
    try {
      setSelectedLog(log);
      const params = {
        fileName: log.fileName,
        pipeLineExecutionId: log.pipeLineExecutionId,
      };
      const data = await get('getSalesOrdersDet', { params });
      setDetalleLog(data);
    } catch (error) {
      toast.error('Error al cargar el detalle del log');
    } 
  };

  const fetchFilteredLogs = async () => {
    try {
      const params = {
        customer: selectedProceso || undefined,
        from: dateRange.from,
        to: dateRange.to,
      };
      const data = await get('getSalesOrders', { params });
      setLogs(data);
    } catch (error) {
      toast.error('Error al cargar los logs');
    }
  };

  useEffect(() => {
    fetchFilteredLogs();
  }, []);

  const filteredLogs = useMemo(() => {
    return logs
      .filter((log) => {
        const matchesCustomer = selectedProceso ? log.customerName === selectedProceso : true;
        const logDate = new Date(log.pipeLineExecutionStartDate);
        const matchesFrom = dateRange.from ? logDate >= new Date(dateRange.from) : true;
        const matchesTo = dateRange.to ? logDate <= new Date(dateRange.to) : true;
        const status = log.traducido?.toLowerCase() || 'na';
        const matchesStatus = statusFilter ? status === statusFilter.toLowerCase() : true;
        return matchesCustomer && matchesFrom && matchesTo && matchesStatus;
      })
      .map((log) => ({
        ...log,
        pipeLineExecutionStartDate: formatDate(log.pipeLineExecutionStartDate),
        pipeLineExecutionEndDate: formatDate(log.pipeLineExecutionEndDate),
      }));
  }, [logs, selectedProceso, dateRange, statusFilter]);

  const statusCounts = useMemo(() => {
    return logs
      .filter((log) => {
        const matchesProceso = selectedProceso ? log.customerName === selectedProceso : true;
        const logDate = new Date(log.pipeLineExecutionStartDate);
        const matchesFrom = dateRange.from ? logDate >= new Date(dateRange.from) : true;
        const matchesTo = dateRange.to ? logDate <= new Date(dateRange.to) : true;
        return matchesProceso && matchesFrom && matchesTo;
      })
      .reduce((acc, log) => {
        const status = log.traducido?.toLowerCase() || 'na';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
  }, [logs, selectedProceso, dateRange]);

  const getStatusConfig = (status) => {
    if (!status) {
      return {
        label: 'PD',
        className: 'bg-gray-200 text-gray-800'
      };
    }

    switch (status) {
      case 'ER':
        return {
          label: 'ER',
          className: 'bg-red-200 text-red-800'
        };
      case 'PR':
        return {
          label: 'PR',
          className: 'bg-yellow-200 text-yellow-800'
        };
      default:
        return {
          label: status,
          className: 'bg-gray-200 text-gray-800'
        };
    }
  };



  const columnsResults = [
    { header: 'Proceso', accessor: 'customerName' },
    {
      header: 'Archivo',
      accessor: 'fileName',
      Cell: ({ value }) => {
        if (!value) return '-';

        const maxLength = 40;
        const shortName =
          value.length > maxLength
            ? value.substring(0, maxLength) + '…'
            : value;

        return (
          <span title={value}>
            {shortName}
          </span>
        );
      }
    },
    {
      header: 'Ordenes',
      accessor: 'purchaseOrdersCount',
      Cell: ({ value }) => <div className="text-right">{value?.toLocaleString() || '0'}</div>,
    },
   
    {
      header: 'Estatus',
      accessor: 'orderStatusFinal',
      Cell: ({ value }) => {
        const { label, className } = getStatusConfig(value);

        return (
          <span
            className={`px-2 py-1 rounded-full text-xs font-semibold ${className}`}
          >
            {label}
          </span>
        );
      }
    },
    {
      header: 'Acciones',
      accessor: 'acciones',
      Cell: ({ row }) => (
        <button
          onClick={() => fetchDetalleLog(row)}
          className="bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
        >
          Detalle
        </button>
      ),
    },
  ];

  const cardsData = [
    {
      label: 'Procesado',
      value: statusCounts.procesado || 0,
      icon: 'mdi:check-circle-outline',
      bgColor: 'bg-green-500',
      textColor: 'text-green-700',
      estatus: 'procesado',
    },
    {
      label: 'Pendientes',
      value: statusCounts.pendiente || 0,
      icon: 'mdi:alert-circle-outline',
      bgColor: 'bg-red-500',
      textColor: 'text-red-700',
      estatus: 'pendiente',
    },
  ];

  const cerrarPanel = () => setSelectedLog(null);

  return (
    <div className="p-4">
      <div className="flex justify-between items-center pb-2 flex-wrap gap-2">
        <h1 className="text-xl font-bold">Pedidos Automáticos</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedProceso}
            onChange={(e) => setSelectedProceso(e.target.value)}
            className="border rounded p-2"
          >
            <option value="">-- Todos los procesos --</option>
            {Array.from(new Set(logs.map((log) => log.customerName))).map((customerName) => (
              <option key={customerName} value={customerName}>
                {customerName}
              </option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="border rounded p-2"
          />
          <input
            type="datetime-local"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="border rounded p-2"
          />
          <button
            onClick={fetchFilteredLogs}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center"
          >
            <Icon icon="mdi:magnify" width="18" height="18" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="flex gap-4">
          {!selectedLog ? (
            <div className="flex-1 bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="mb-4 pt-4 px-4">
                <InfoCards cardsData={cardsData} estatusActivo={statusFilter} onCardClick={setStatusFilter} />
              </div>
              <div className="p-4 text-xs overflow-x-auto">
                <div className="min-w-[800px]">
                  <TableWithPagination
                    columns={columnsResults}
                    data={filteredLogs}
                    showView={false}
                    showEdit={false}
                    showDelete={false}
                    loading={false}
                  />
                </div>
              </div>
            </div>
          ) : (
            <DetalleLogPanel
              log={selectedLog}
              detalle={detalleLog}
              loading={loadingDetalle}
              onClose={cerrarPanel}
            />
          )}
        </div>
      </div> 
    </div>
  );
};

export default Traduccion;
